// src/services/SupabaseDataService.ts
/**
 * 💾 Supabase Data Service - ROBUST VERSION
 * Handles CRUD operations for trading files and trades with retry logic and validation
 */

import { supabase } from '../config/supabase';
import { StandardTrade } from '../types/database';
import { StandardFile } from '../components/DataFilesContext';
import { RobustSupabaseClient } from './RobustSupabaseClient';
import { ErrorLoggerService } from './ErrorLoggerService';
import { ValidationService, Validators } from './ValidationService';
import { invalidateCache } from './RobustnessUtils';
import { resolveTradePnl, toFiniteTradeNumber } from '../utils/tradePnl';

/**
 * Fields that have a real column on `trades`, and so must NOT be duplicated
 * into `custom_data`.
 *
 * `notes` and `strategy` used to be in here and were the only two entries that
 * were not true: there is no column for either, and the insert below never
 * wrote them. Being listed as "standard" excluded them from `custom_data` as
 * well, so a trade imported with notes lost them silently — while editing the
 * same field afterwards kept it, because `updateTrade` keeps its own shorter
 * list and let them fall through to `custom_data`. Two write paths disagreeing
 * about the same field, one of them losing data.
 *
 * They now go to `custom_data` on both paths. `mapDbTrade` spreads it back over
 * the trade on read, so nothing else has to change and no migration is needed.
 */
const STANDARD_TRADE_KEYS = new Set([
  'id', 'symbol', 'type', 'date', 'profit', 'netProfit', 'netPnl',
  'quantity', 'entryPrice', 'exitPrice', 'entryTime', 'exitTime', 'exitDate',
  'commission', 'swap', 'durationSec', 'stopLoss', 'takeProfit',
  'customFields', 'custom_data', 'customData'
]);

const optionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  return toFiniteTradeNumber(value);
};

const buildTradeCustomData = (trade: any): Record<string, any> => {
  const customData: Record<string, any> = {
    ...(trade?.custom_data || {}),
    ...(trade?.customData || {}),
    ...(trade?.customFields || {}),
  };

  Object.entries(trade || {}).forEach(([key, value]) => {
    if (!STANDARD_TRADE_KEYS.has(key) && value !== undefined) {
      customData[key] = value;
    }
  });

  return customData;
};

const mapDbTrade = (t: any): StandardTrade => {
  const rawTrade = {
    ...(t.custom_data || {}),
    custom_data: t.custom_data || {},
    profit: t.profit,
    netProfit: t.net_pnl ?? t.profit,
    netPnl: t.net_pnl,
    commission: t.commission,
  };
  const pnl = resolveTradePnl(rawTrade);

  return {
    ...(t.custom_data || {}),
    custom_data: t.custom_data || undefined,
    id: t.id,
    symbol: t.symbol,
    type: t.direction as 'BUY' | 'SELL' | 'LONG' | 'SHORT',
    date: t.entry_time ? t.entry_time.split('T')[0] : t.entry_time,
    profit: pnl,
    netProfit: pnl,
    netPnl: pnl,
    quantity: optionalNumber(t.quantity),
    entryPrice: optionalNumber(t.entry_price),
    exitPrice: optionalNumber(t.exit_price),
    entryTime: t.entry_time ? t.entry_time.split('T')[1]?.replace('Z', '') : undefined,
    exitTime:  t.exit_time  ? t.exit_time.split('T')[1]?.replace('Z', '')  : undefined,
    exitDate:  t.exit_time  ? t.exit_time.split('T')[0]                    : undefined,
    commission: optionalNumber(t.commission),
  } as StandardTrade;
};

export class SupabaseDataService {
  
  /**
   * Upload CSV file with all trades
   */
  static async updateFileCapital(fileId: string, capital: number): Promise<{ success: boolean; error?: string }> {
    try {
      // Fetch existing metadata
      const { data, error: fetchErr } = await supabase
        .from('trading_files')
        .select('metadata')
        .eq('id', fileId)
        .single();
      if (fetchErr) return { success: false, error: fetchErr.message };

      const updatedMetadata = { ...(data?.metadata || {}), initialCapital: capital };
      const { error: updateError } = await supabase
        .from('trading_files')
        .update({ metadata: updatedMetadata })
        .eq('id', fileId);

      invalidateCache('user-files');
      if (updateError) return { success: false, error: updateError.message };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  static async updateFileName(fileId: string, name: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error: updateError } = await supabase
        .from('trading_files')
        .update({ name })
        .eq('id', fileId);

      invalidateCache('user-files');
      if (updateError) return { success: false, error: updateError.message };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  static async uploadFile(
    fileName: string,
    trades: StandardTrade[],
    initialCapital?: number
  ): Promise<{
    success: boolean;
    error?: string;
    fileId?: string;
    tradesCount?: number;
  }> {
    try {
      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      // 2. Calculate metadata
      const sortedTrades = [...trades].sort((a, b) => 
        new Date(`${a.date}T${a.entryTime||'00:00:00'}`).getTime() - new Date(`${b.date}T${b.entryTime||'00:00:00'}`).getTime()
      );

      const metadata = {
        totalTrades: trades.length,
        dateRange: {
          from: sortedTrades[0]?.entryTime || sortedTrades[0]?.date || new Date().toISOString(),
          to: sortedTrades[sortedTrades.length - 1]?.exitTime || 
              sortedTrades[sortedTrades.length - 1]?.date || 
              new Date().toISOString()
        },
        uploadedBy: user.email
      };

      // 3. Create file record with retry logic
      const { data: file, error: fileError } = await RobustSupabaseClient.insert(
        'trading_files',
        {
          user_id: user.id,
          name: fileName,
          metadata: metadata
        },
        {
          enableRetry: true,
          maxRetries: 2,
          enableCircuitBreaker: true,
          circuitBreakerName: 'trading-files-insert',
          componentName: 'SupabaseDataService',
          actionName: 'uploadFile-createFile'
        }
      );

      if (fileError) {
        console.error('❌ File creation error:', fileError);
        await ErrorLoggerService.logDatabaseError(
          `File creation failed: ${fileError.message}`,
          'trading_files',
          'INSERT',
          'SupabaseDataService'
        );
        return { success: false, error: `File creation failed: ${fileError.message}` };
      }

      // 4. Prepare trades data with timestamp validation
      const tradesData = trades.map(trade => {
        // 🔧 FIX: Helper to combine date + time or validate full timestamps
        const fixTimestamp = (timeValue: any, dateValue: string): string => {
          if (!timeValue) {
            // No time provided, use date as full datetime
            try {
              const parsed = new Date(dateValue);
              return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
            } catch {
              return new Date().toISOString();
            }
          }

          const str = String(timeValue);

          // If it's just time (HH:MM:SS), combine with trade.date
          if (/^\d{1,2}:\d{2}/.test(str) && !str.includes('T') && !str.includes('/') && !/^\d{4}\./.test(str)) {
            const dateOnly = dateValue.split('T')[0];
            const timePart = str.includes('.') ? str.split('.')[0] : str;
            return `${dateOnly}T${timePart}Z`;
          }

          // MT4/MT5 dot format: "2024.01.02 08:15:32"
          if (/^\d{4}\.\d{2}\.\d{2}[\sT]/.test(str)) {
            const sepIdx = str.search(/[\sT]/);
            const datePart = str.substring(0, sepIdx).replace(/\./g, '-');
            const timePart = str.substring(sepIdx + 1).split('.')[0];
            const parsed = new Date(`${datePart}T${timePart}Z`);
            return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
          }

          // If it's already ISO, return as is
          if (str.includes('T') || str.includes('Z')) return str;

          // Try to parse it as full date
          try {
            const parsed = new Date(str);
            return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
          } catch {
            return new Date().toISOString();
          }
        };

        // 🔧 FIX: Use trade.date as base, NOT current timestamp
        const baseDate = trade.date || new Date().toISOString().split('T')[0];
        const entryTime = fixTimestamp(trade.entryTime, baseDate);
        const exitTime = fixTimestamp(trade.exitTime, trade.exitDate || baseDate);
        const pnl = resolveTradePnl(trade);
        const customData = buildTradeCustomData(trade);

        return {
          file_id: (file as any).id,
          user_id: user.id,
          symbol: trade.symbol || 'UNKNOWN',
          direction: trade.type || 'UNKNOWN', // Use 'type' field
          quantity: trade.quantity || 0,
          entry_price: (trade.entryPrice != null && trade.entryPrice !== 0) ? trade.entryPrice : null,
          exit_price: (trade.exitPrice != null && trade.exitPrice !== 0) ? trade.exitPrice : null,
          entry_time: entryTime,
          exit_time: exitTime,
          profit: pnl,
          commission: trade.commission || 0,
          custom_data: Object.keys(customData).length > 0 ? customData : {}
          // net_pnl is GENERATED column - will be calculated automatically by database
        };
      });

      // 🐛 DEBUG: Log first 3 trades to verify decimal preservation before DB insert
      if (tradesData.length > 0) {
        console.log('💾 Sample trades before DB insert (first 3):');
        tradesData.slice(0, 3).forEach((trade, idx) => {
          console.log(`  Trade ${idx + 1}:`, {
            symbol: trade.symbol,
            entry_price: trade.entry_price,
            exit_price: trade.exit_price,
            entry_price_type: typeof trade.entry_price,
            exit_price_type: typeof trade.exit_price
          });
        });
      }

      // 5. Insert trades in batches with retry logic (Supabase limit: 1000 per request)
      const batchSize = 500;
      for (let i = 0; i < tradesData.length; i += batchSize) {
        const batch = tradesData.slice(i, i + batchSize);

        const { data: insertedTrades, error: tradesError } = await RobustSupabaseClient.insert(
          'trades',
          batch,
          {
            enableRetry: true,
            maxRetries: 3,
            timeoutMs: 30000, // 30s for batch inserts
            enableCircuitBreaker: true,
            circuitBreakerName: 'trades-batch-insert',
            componentName: 'SupabaseDataService',
            actionName: `uploadFile-insertBatch-${i / batchSize + 1}`
          }
        );

        if (tradesError) {
          console.error('❌ Trades insertion error:', tradesError);
          await ErrorLoggerService.logDatabaseError(
            `Trades batch ${i / batchSize + 1} insertion failed: ${tradesError.message}`,
            'trades',
            'BATCH_INSERT',
            'SupabaseDataService'
          );
          // Rollback: delete file if trades insertion fails
          await RobustSupabaseClient.delete('trading_files', { id: (file as any).id }, { enableRetry: false });
          return { success: false, error: `Trades insertion failed: ${tradesError.message}` };
        }
      }

      console.log(`✅ Uploaded file "${fileName}" with ${trades.length} trades`);

      // 🔄 Invalidate cache to ensure file appears immediately
      invalidateCache('user-files');

      return {
        success: true,
        fileId: (file as any).id,
        tradesCount: trades.length
      };

    } catch (error: any) {
      console.error('❌ Upload exception:', error);
      return { 
        success: false, 
        error: error.message || 'Upload failed' 
      };
    }
  }

  /**
   * Like `getUserFiles`, but it says whether the read actually happened.
   *
   * `getUserFiles` answers `[]` to two different questions — "this account has
   * no files" and "I could not reach the database" — and callers cannot tell
   * them apart. One caller in particular decides from that answer whether to
   * install the demo files and start onboarding, so a timeout on
   * `trading_files` turned an account with hundreds of trades into a brand-new
   * one, complete with demo data and the first-run tour.
   *
   * The product is a web app. When the server cannot be reached the right thing
   * is to say so, not to guess that the user is new.
   */
  static async getUserFilesWithStatus(): Promise<
    { ok: true; files: StandardFile[] } | { ok: false; error: string }
  > {
    try {
      const files = await this.getUserFiles({ throwOnFailure: true });
      return { ok: true, files };
    } catch (error: any) {
      return { ok: false, error: error?.message || 'Could not reach the database' };
    }
  }

  /**
   * Get all user files with trades - WITH ROBUST RETRY & CACHING
   *
   * Returns `[]` on failure for backwards compatibility with its nine callers.
   * Pass `throwOnFailure` when the difference between "empty" and "unreachable"
   * matters — see `getUserFilesWithStatus`.
   */
  static async getUserFiles(opts?: { throwOnFailure?: boolean }): Promise<StandardFile[]> {
    try {
      const { data: files, error } = await RobustSupabaseClient.select(
        'trading_files',
        (builder) => builder
          .select(`
            id,
            name,
            uploaded_at,
            metadata,
            trades!left (
              id,
              symbol,
              direction,
              quantity,
              entry_price,
              exit_price,
              entry_time,
              exit_time,
              profit,
              commission,
              net_pnl,
              custom_data
            )
          `)
          // Recycle bin: never list files the user has deleted (see migration 060)
          .is('deleted_at', null)
          .order('uploaded_at', { ascending: false })
          .order('entry_time', { foreignTable: 'trades', ascending: true }),
        {
          enableRetry: true,
          maxRetries: 3,
          enableCache: true,
          cacheTtlMs: 30000, // 30 seconds cache
          cacheKey: 'user-files',
          componentName: 'SupabaseDataService',
          actionName: 'getUserFiles'
        }
      );

      if (error) {
        console.error('❌ Error fetching files:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to fetch user files: ${error.message}`,
          'trading_files',
          'SELECT',
          'SupabaseDataService'
        );
        if (opts?.throwOnFailure) throw new Error(error.message || 'Failed to fetch user files');
        return [];
      }

      if (!files || files.length === 0) {
        console.log('📁 No files found for user');
        return [];
      }

      // Transform to StandardFile format
      const standardFiles: StandardFile[] = (files as any[]).map((file: any) => {
        // 🔧 FIX: Sort trades by entry_time to maintain chronological order
        // Handle null trades (empty files with LEFT JOIN)
        const sortedTrades = (file.trades || [])
          .filter((t: any) => t && t.id) // Filter out null entries from LEFT JOIN
          .map(mapDbTrade)
          .sort((a, b) => {
            const timeA = new Date(`${a.date || '1970-01-01'}T${a.entryTime || '00:00:00'}`).getTime();
            const timeB = new Date(`${b.date || '1970-01-01'}T${b.entryTime || '00:00:00'}`).getTime();
            return timeA - timeB;
          });

        return {
          id: (file as any).id,
          name: file.name,
          trades: sortedTrades,
          initialCapital: file.metadata?.initialCapital,
          customColumns: file.metadata?.customColumns || [],
          metadata: {
            uploadedAt: file.uploaded_at,
            totalTrades: sortedTrades.length,
            dateRange: file.metadata?.dateRange || { from: '', to: '' },
            platform: file.metadata?.platform || null,
            confidence: file.metadata?.confidence || null,
            unmappedColumns: file.metadata?.unmappedColumns || [],
            initialCapital: file.metadata?.initialCapital
          }
        };
      });

      console.log(`✅ Loaded ${standardFiles.length} files with ${standardFiles.reduce((sum, f) => sum + f.trades.length, 0)} total trades`);
      
      return standardFiles;

    } catch (error: any) {
      console.error('❌ Error loading files:', error);
      if (opts?.throwOnFailure) throw error;
      return [];
    }
  }

  /**
   * Update existing file by adding new trades (merge)
   */
  static async updateFileWithNewTrades(
    fileId: string,
    newTrades: StandardTrade[]
  ): Promise<{
    success: boolean;
    error?: string;
    addedCount?: number;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      const normalizePrice = (value: unknown): string => {
        const num = optionalNumber(value);
        return num === undefined ? 'na' : num.toFixed(8);
      };

      const fixTimestamp = (timeValue: any, dateValue: string): string => {
        if (!timeValue) {
          try {
            const parsed = new Date(dateValue);
            return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
          } catch {
            return '';
          }
        }

        const str = String(timeValue);

        if (/^\d{1,2}:\d{2}/.test(str) && !str.includes('T') && !str.includes('/') && !/^\d{4}\./.test(str)) {
          const dateOnly = dateValue.split('T')[0];
          const timePart = str.includes('.') ? str.split('.')[0] : str;
          const parsed = new Date(`${dateOnly}T${timePart}Z`);
          return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
        }

        if (/^\d{4}\.\d{2}\.\d{2}[\sT]/.test(str)) {
          const sepIdx = str.search(/[\sT]/);
          const datePart = str.substring(0, sepIdx).replace(/\./g, '-');
          const timePart = str.substring(sepIdx + 1).split('.')[0];
          const parsed = new Date(`${datePart}T${timePart}Z`);
          return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
        }

        try {
          const parsed = new Date(str);
          return isNaN(parsed.getTime()) ? str : parsed.toISOString();
        } catch {
          return str;
        }
      };

      const tradeMergeKey = (trade: StandardTrade): string => {
        const baseDate = trade.date || new Date().toISOString().split('T')[0];
        return [
          (trade.symbol || '').trim().toUpperCase(),
          fixTimestamp(trade.entryTime || trade.date, baseDate),
          normalizePrice(trade.entryPrice),
          String(trade.type || '').trim().toUpperCase()
        ].join('|');
      };

      // Get existing trades for this file
      const { data: existingTrades, error: fetchError } = await supabase
        .from('trades')
        .select('entry_time, exit_time, symbol, entry_price, direction')
        .eq('file_id', fileId);

      if (fetchError) {
        console.error('❌ Error fetching existing trades:', fetchError);
        return { success: false, error: fetchError.message };
      }

      // Filter out duplicate trades based on entry_time, symbol, and entry_price
      const existingTradeKeys = new Set(
        (existingTrades || []).map(t =>
          [
            (t.symbol || '').trim().toUpperCase(),
            fixTimestamp(t.entry_time, t.entry_time || ''),
            normalizePrice(t.entry_price),
            String(t.direction || '').trim().toUpperCase()
          ].join('|')
        )
      );

      const uniqueNewTrades = newTrades.filter(trade => {
        return !existingTradeKeys.has(tradeMergeKey(trade));
      });

      console.log(`📊 Trade analysis:
        - Existing: ${existingTrades?.length || 0}
        - New file: ${newTrades.length}
        - Unique new: ${uniqueNewTrades.length}
        - Duplicates skipped: ${newTrades.length - uniqueNewTrades.length}
      `);

      if (uniqueNewTrades.length === 0) {
        return {
          success: true,
          addedCount: 0,
          error: 'No new trades to add (all duplicates)'
        };
      }

      // Prepare trades data
      const tradesData = uniqueNewTrades.map(trade => {
        // 🔧 FIX: Use trade.date as base, NOT current timestamp
        const baseDate = trade.date || new Date().toISOString().split('T')[0];
        const entryTime = fixTimestamp(trade.entryTime || trade.date, baseDate) || new Date().toISOString();
        const exitTime = fixTimestamp(trade.exitTime || trade.exitDate, trade.exitDate || baseDate) || entryTime;
        const pnl = resolveTradePnl(trade);

        // Extract custom fields (any field not in standard schema)
        const customData = buildTradeCustomData(trade);

        return {
          file_id: fileId,
          user_id: user.id,
          symbol: trade.symbol || 'UNKNOWN',
          direction: trade.type || 'UNKNOWN',
          quantity: trade.quantity || 0,
          entry_price: (trade.entryPrice != null && trade.entryPrice !== 0) ? trade.entryPrice : null,
          exit_price: (trade.exitPrice != null && trade.exitPrice !== 0) ? trade.exitPrice : null,
          entry_time: entryTime,
          exit_time: exitTime,
          profit: pnl,
          commission: trade.commission || 0,
          custom_data: Object.keys(customData).length > 0 ? customData : {}
        };
      });

      // Insert new trades in batches
      const batchSize = 500;
      for (let i = 0; i < tradesData.length; i += batchSize) {
        const batch = tradesData.slice(i, i + batchSize);

        const { error: insertError } = await supabase
          .from('trades')
          .insert(batch);

        if (insertError) {
          console.error('❌ Trades insertion error:', insertError);
          return { success: false, error: `Trades insertion failed: ${insertError.message}` };
        }
      }

      // Update file metadata - 🔧 FIX: Preserve existing metadata (especially customColumns)
      const allTrades = [...(existingTrades || []), ...uniqueNewTrades];
      const toTs = (t: any) => {
        if (t.entry_time) return new Date(t.entry_time).getTime();
        return new Date(`${t.date||'1970-01-01'}T${t.entryTime||'00:00:00'}`).getTime();
      };
      const sortedTrades = allTrades.sort((a, b) => toTs(a) - toTs(b));

      // Get existing file to preserve metadata
      const { data: existingFile } = await supabase
        .from('trading_files')
        .select('metadata')
        .eq('id', fileId)
        .single();

      const metadata = {
        ...(existingFile?.metadata || {}), // Preserve existing metadata (including customColumns)
        totalTrades: allTrades.length,
        dateRange: {
          from: (sortedTrades[0] as any)?.entry_time || (sortedTrades[0] as any)?.entryTime || (sortedTrades[0] as any)?.date || new Date().toISOString(),
          to: (sortedTrades[sortedTrades.length - 1] as any)?.exit_time ||
              (sortedTrades[sortedTrades.length - 1] as any)?.exitTime ||
              (sortedTrades[sortedTrades.length - 1] as any)?.date ||
              new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('trading_files')
        .update({ metadata })
        .eq('id', fileId);

      if (updateError) {
        console.warn('⚠️ Warning: Could not update file metadata:', updateError);
      }

      console.log(`✅ Added ${uniqueNewTrades.length} new trades to file ${fileId}`);

      // 🔄 Invalidate cache to ensure file updates appear immediately
      invalidateCache('user-files');

      return {
        success: true,
        addedCount: uniqueNewTrades.length
      };

    } catch (error: any) {
      console.error('❌ Update file exception:', error);
      return {
        success: false,
        error: error.message || 'Update failed'
      };
    }
  }

  /**
   * Check if a file with the same name exists
   */
  static async findFileByName(fileName: string): Promise<{
    exists: boolean;
    fileId?: string;
    file?: StandardFile;
  }> {
    try {
      const files = await this.getUserFiles();
      const existingFile = files.find(f => f.name === fileName);

      if (existingFile) {
        return {
          exists: true,
          fileId: existingFile.id,
          file: existingFile
        };
      }

      return { exists: false };

    } catch (error: any) {
      console.error('❌ Error finding file:', error);
      return { exists: false };
    }
  }

  /**
   * Delete file and all its trades (cascade) - WITH ROBUST RETRY
   */
  static async deleteFile(fileId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Validate UUID format
      const validation = ValidationService.validate({ fileId }, [
        {
          field: 'fileId',
          validators: [
            Validators.required('File ID is required'),
            Validators.uuid('Invalid file ID format')
          ]
        }
      ]);

      if (!validation.isValid) {
        const errorMsg = validation.errors.fileId?.[0] || 'Invalid file ID';
        await ErrorLoggerService.logValidationError(errorMsg, 'fileId', fileId, 'SupabaseDataService');
        return { success: false, error: errorMsg };
      }

      // SOFT delete: stamps deleted_at and keeps the row (and its trades) for 30
      // days so the user can restore it from Settings → Deleted files.
      // See migration 060_trading_files_soft_delete.sql.
      const { data, error } = await supabase.rpc('soft_delete_trading_file', { p_file_id: fileId });

      if (error) {
        console.error('❌ Delete error:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to delete file ${fileId}: ${error.message}`,
          'trading_files',
          'UPDATE',
          'SupabaseDataService'
        );
        return { success: false, error: error.message };
      }

      if (data && data.ok === false) {
        return { success: false, error: String(data.error || 'Could not delete file') };
      }

      console.log(`🗑️ Moved file to recycle bin (restorable 30 days): ${fileId}`);

      // 🔄 Invalidate cache to ensure file disappears immediately
      invalidateCache('user-files');

      return { success: true };

    } catch (error: any) {
      console.error('❌ Delete exception:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SupabaseDataService', 'deleteFile');
      return {
        success: false,
        error: error.message || 'Delete failed'
      };
    }
  }

  // ── Recycle bin (30-day recovery) ─────────────────────────────────────────

  /** Files the user deleted and can still restore, newest first. */
  static async listDeletedFiles(): Promise<Array<{
    id: string;
    name: string;
    deletedAt: string;
    expiresAt: string;
    daysLeft: number;
  }>> {
    try {
      const { data, error } = await supabase.rpc('list_deleted_trading_files');
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        deletedAt: row.deleted_at,
        expiresAt: row.expires_at,
        daysLeft: Number(row.days_left ?? 0),
      }));
    } catch (error: any) {
      console.error('❌ Error listing deleted files:', error.message);
      return [];
    }
  }

  /** Bring a file back from the recycle bin (only within the 30-day window). */
  static async restoreFile(fileId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('restore_trading_file', { p_file_id: fileId });
      if (error) return { success: false, error: error.message };
      if (data && data.ok === false) {
        return { success: false, error: String(data.error || 'Could not restore file') };
      }
      invalidateCache('user-files');
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Restore failed' };
    }
  }

  /** Delete a binned file for good. Irreversible. */
  static async purgeFile(fileId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('purge_trading_file', { p_file_id: fileId });
      if (error) return { success: false, error: error.message };
      if (data && data.ok === false) {
        return { success: false, error: 'File not found in the recycle bin' };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Purge failed' };
    }
  }

  /**
   * Get all trades for user (across all files)
   */
  static async getAllTrades(): Promise<StandardTrade[]> {
    try {
      const { data: trades, error } = await supabase
        .from('trades')
        .select('*')
        .order('entry_time', { ascending: true });

      if (error) {
        console.error('❌ Error fetching trades:', error);
        return [];
      }

      return (trades || []).map(mapDbTrade);

    } catch (error: any) {
      console.error('❌ Error loading trades:', error);
      return [];
    }
  }

  /**
   * Get trades for specific file
   */
  static async getFileTrades(fileId: string): Promise<StandardTrade[]> {
    try {
      const { data: trades, error } = await supabase
        .from('trades')
        .select('*')
        .eq('file_id', fileId)
        .order('entry_time', { ascending: true });

      if (error) {
        console.error('❌ Error fetching file trades:', error);
        return [];
      }

      return (trades || []).map(mapDbTrade);

    } catch (error: any) {
      console.error('❌ Error loading file trades:', error);
      return [];
    }
  }

  /**
   * Get user statistics - WITH ROBUST RPC & CACHING
   */
  static async getUserStats(): Promise<{
    totalFiles: number;
    totalTrades: number;
    totalPnL: number;
    lastUpload: string | null;
  }> {
    try {
      const { data: stats, error } = await RobustSupabaseClient.rpc(
        'get_user_stats',
        undefined,
        {
          enableRetry: true,
          maxRetries: 3,
          enableCircuitBreaker: false, // No circuit breaker for stats
          enableCache: true,
          cacheTtlMs: 60000, // 1 minute cache
          cacheKey: 'user-stats',
          componentName: 'SupabaseDataService',
          actionName: 'getUserStats'
        }
      );

      if (error) {
        console.error('❌ Error fetching stats:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to fetch user stats: ${error.message}`,
          'get_user_stats',
          'RPC',
          'SupabaseDataService'
        );
        // Fallback: calculate manually
        const files = await this.getUserFiles();
        const totalTrades = files.reduce((sum, f) => sum + f.trades.length, 0);
        const totalPnL = files
          .flatMap(f => f.trades)
          .reduce((sum, t) => sum + (t.profit - (t.commission || 0)), 0);

        return {
          totalFiles: files.length,
          totalTrades,
          totalPnL,
          lastUpload: files[0]?.metadata.uploadedAt || null
        };
      }

      return stats as any;

    } catch (error: any) {
      console.error('❌ Error loading stats:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SupabaseDataService', 'getUserStats');
      return {
        totalFiles: 0,
        totalTrades: 0,
        totalPnL: 0,
        lastUpload: null
      };
    }
  }

  /**
   * 🔄 Update file metadata (generic update method)
   * Updates metadata fields of a trading file (NOT trades)
   */
  static async updateFile(
    fileId: string,
    updates: { name?: string; metadata?: any; customColumns?: any[] }
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Prepare updates object
      const dbUpdates: any = {};

      // If name is provided, update it
      if (updates.name !== undefined) {
        dbUpdates.name = updates.name;
      }

      // Handle customColumns - save them inside metadata.customColumns
      if (updates.customColumns !== undefined) {
        // First get current metadata
        const { data: file } = await supabase
          .from('trading_files')
          .select('metadata')
          .eq('id', fileId)
          .eq('user_id', user.id)
          .single();

        const currentMetadata = file?.metadata || {};
        dbUpdates.metadata = {
          ...currentMetadata,
          ...updates.metadata,
          customColumns: updates.customColumns
        };
      } else if (updates.metadata !== undefined) {
        // If only metadata is provided (not customColumns), merge it
        const { data: file } = await supabase
          .from('trading_files')
          .select('metadata')
          .eq('id', fileId)
          .eq('user_id', user.id)
          .single();

        const currentMetadata = file?.metadata || {};
        dbUpdates.metadata = {
          ...currentMetadata,
          ...updates.metadata
        };
      }

      // Update file in database
      const { error: updateError } = await supabase
        .from('trading_files')
        .update(dbUpdates)
        .eq('id', fileId)
        .eq('user_id', user.id);

      if (updateError) {
        console.error('❌ Error updating file:', updateError);
        return { success: false, error: updateError.message };
      }

      // Every other write in this service drops the cached file list; this one
      // did not. So a caller that updated a file and then refreshed was served
      // the pre-update copy from cache and wrote it back over localStorage —
      // undoing the change it had just saved. That is what made "add column"
      // look broken: the column reached the database and was then overwritten
      // locally by a stale read, a second later.
      invalidateCache('user-files');

      console.log(`✅ File ${fileId} updated successfully`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ Error in updateFile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update a single trade
   */
  static async updateTrade(
    tradeId: string,
    updates: Partial<StandardTrade>
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Standard fields that map directly to database columns
      const standardFields = ['symbol', 'type', 'quantity', 'entryPrice', 'exitPrice', 'entryTime', 'exitTime', 'profit', 'commission'];

      // Map StandardTrade fields to database schema
      const dbUpdates: any = {};
      const customData: any = {};

      // First, get the trade to access its date for time conversion
      let tradeDate: string | null = null;
      if (updates.entryTime || updates.exitTime) {
        const { data: trade } = await supabase
          .from('trades')
          .select('entry_time')
          .eq('id', tradeId)
          .eq('user_id', user.id)
          .single();

        tradeDate = trade?.entry_time ? new Date(trade.entry_time).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      }

      // Process each update
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined) continue;

        // Map standard fields to database columns
        if (key === 'symbol') dbUpdates.symbol = value;
        else if (key === 'type') dbUpdates.direction = value;
        else if (key === 'quantity') dbUpdates.quantity = value;
        else if (key === 'entryPrice') dbUpdates.entry_price = value;
        else if (key === 'exitPrice') dbUpdates.exit_price = value;
        else if (key === 'entryTime') {
          // Convert time-only format (HH:MM:SS) to timestamp
          if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
            // Create Date object from date + time (interpreted as LOCAL timezone)
            const localDateTime = new Date(`${tradeDate}T${value}`);
            // Convert to UTC timestamp (toISOString automatically converts to UTC)
            dbUpdates.entry_time = localDateTime.toISOString();
            console.log(`🕐 Converted entryTime: ${value} (local) → ${dbUpdates.entry_time} (UTC)`);
          } else {
            dbUpdates.entry_time = value;
          }
        }
        else if (key === 'exitTime') {
          // Convert time-only format (HH:MM:SS) to timestamp
          if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
            // Create Date object from date + time (interpreted as LOCAL timezone)
            const localDateTime = new Date(`${tradeDate}T${value}`);
            // Convert to UTC timestamp (toISOString automatically converts to UTC)
            dbUpdates.exit_time = localDateTime.toISOString();
            console.log(`🕐 Converted exitTime: ${value} (local) → ${dbUpdates.exit_time} (UTC)`);
          } else {
            dbUpdates.exit_time = value;
          }
        }
        else if (key === 'profit') dbUpdates.profit = value;
        else if (key === 'commission') dbUpdates.commission = value;
        // Any other fields are custom columns
        else {
          customData[key] = value;
        }
      }

      // If we have custom data, we need to merge it with existing custom_data
      if (Object.keys(customData).length > 0) {
        // First get current custom_data
        const { data: trade } = await supabase
          .from('trades')
          .select('custom_data')
          .eq('id', tradeId)
          .eq('user_id', user.id)
          .single();

        const currentCustomData = trade?.custom_data || {};

        // Merge with new custom data
        dbUpdates.custom_data = {
          ...currentCustomData,
          ...customData
        };

        console.log('💾 Saving custom data:', dbUpdates.custom_data);
      }

      const { error: updateError } = await supabase
        .from('trades')
        .update(dbUpdates)
        .eq('id', tradeId)
        .eq('user_id', user.id);

      if (updateError) {
        console.error('❌ Error updating trade:', updateError);
        return { success: false, error: updateError.message };
      }

      console.log(`✅ Trade ${tradeId} updated successfully`);

      // 🔄 Invalidate cache to ensure updates appear immediately
      invalidateCache('user-files');

      return { success: true };

    } catch (error: any) {
      console.error('❌ Error in updateTrade:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete trades by IDs
   */
  static async deleteTrades(
    tradeIds: string[]
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error: deleteError } = await supabase
        .from('trades')
        .delete()
        .in('id', tradeIds)
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('❌ Error deleting trades:', deleteError);
        return { success: false, error: deleteError.message };
      }

      console.log(`✅ Deleted ${tradeIds.length} trade(s)`);

      // 🔄 Invalidate cache to ensure deletions appear immediately
      invalidateCache('user-files');

      return { success: true };

    } catch (error: any) {
      console.error('❌ Error in deleteTrades:', error);
      return { success: false, error: error.message };
    }
  }
}
